import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Text, View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {MediaLayout} from "@khanacademy/wonder-blocks-layout";
import {border, semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingLarge,
    HeadingMedium,
    LabelMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {useGuideState} from "@khan/guide-state";
import type {KAError} from "@khan/logging";

import {type ToolRequestedInput} from "../types.ts";

import {RequestedInput} from "./requested-input.tsx";
import {SubmissionErrorBanner} from "./submission-error-banner.tsx";

interface Props {
    title: string;
    description: string;
    footer?: string;
    color: string;
    icon: PhosphorRegular;
    submitText: string;
    inputs: readonly ToolRequestedInput[];
    initialValues: Record<string, string> | null;
    submitting: boolean;
    submitError: KAError | null;
    onSubmit: (values: Record<string, string>) => void;
}

export const InputsForm = ({
    title,
    description,
    footer,
    color,
    icon,
    submitText,
    inputs,
    initialValues,
    submitting,
    submitError,
    onSubmit,
}: Props): React.ReactNode => {
    const [values, setValues] = useState<Record<string, string>>(() =>
        Object.fromEntries(
            inputs.map((input) => [
                input.variableName,
                initialValues?.[input.variableName] || input.initialValue || "",
            ]),
        ),
    );

    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const threadId = useGuideState("threadId");
    const navigation = useGuideState("navigation");

    const isAnyRequiredInputMissing = inputs.some((i) => {
        const currentValue = values[i.variableName] || "";
        return i.required && currentValue.trim().length < 1;
    });

    const handleClick = () => {
        publishAnalyticsEvent({
            eventType: "AiGuideCoeditorGenerateClicked",
            eventSchemaVersion: 1,
            aiGuideThreadId: threadId ?? "",
            aiGuideNavigationId: navigation.id,
        });

        onSubmit(values);
    };

    return (
        <MediaLayout>
            {({mediaSize}) => (
                <View style={styles.container}>
                    {mediaSize === "small" ? (
                        <View
                            style={[
                                styles.header,
                                styles.headerSmall,
                                {background: color},
                            ]}
                        >
                            <HeadingMedium>{title}</HeadingMedium>
                            <LabelSmall style={styles.description}>
                                {description}
                            </LabelSmall>
                        </View>
                    ) : (
                        <View style={[styles.header, {background: color}]}>
                            <PhosphorIcon icon={icon} size="large" />
                            <HeadingLarge>{title}</HeadingLarge>
                            <LabelMedium style={styles.description}>
                                {description}
                            </LabelMedium>
                        </View>
                    )}
                    <View style={styles.inputs} testId="ai-tool-form">
                        {inputs.map((input) => (
                            <RequestedInput
                                key={input.variableName}
                                input={input}
                                values={values}
                                setValues={setValues}
                            />
                        ))}

                        {footer && <Text>{footer}</Text>}

                        {submitError && (
                            <SubmissionErrorBanner submitError={submitError} />
                        )}

                        <Button
                            disabled={isAnyRequiredInputMissing || submitting}
                            type="submit"
                            onClick={handleClick}
                            spinner={submitting}
                        >
                            {submitText}
                        </Button>
                    </View>
                </View>
            )}
        </MediaLayout>
    );
};

const styles = StyleSheet.create({
    container: {
        borderRadius: border.radius.radius_040,
        background: semanticColor.surface.primary,
        border: `${border.width.thin} solid ${semanticColor.core.border.neutral.subtle}`,
        alignSelf: "center",
        width: "100%",
        maxWidth: `calc(${sizing.size_080} * 100)`,
        flexShrink: 0,
    },
    header: {
        padding: sizing.size_320,
        borderStartStartRadius: border.radius.radius_040,
        borderStartEndRadius: border.radius.radius_040,
        borderBlockEnd: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        alignItems: "center",
        flexShrink: 0,
        gap: sizing.size_120,
    },
    headerSmall: {
        padding: `${sizing.size_160} ${sizing.size_080}`,
        gap: sizing.size_080,
    },
    description: {
        width: "100%",
        maxWidth: `calc(${sizing.size_080} * 50)`,
        textAlign: "center",
    },
    inputs: {
        width: "100%",
        maxWidth: `calc(${sizing.size_080} * 64)`,
        padding: sizing.size_320,
        margin: `0 auto`,
        flexShrink: 0,
        gap: sizing.size_240,
    },
});
